import { normalizeText, stemToken } from "./parser";
import { featureLabel } from "./features";
import { rvTypeLabel, rvTypeMatches } from "./rvTypes";
import type {
  ParsedQuery,
  QueryLanguageConfig,
  ScoreBreakdown,
  SearchDoc,
  SearchHit,
} from "./types";

function eq(a: string | null | undefined, b: string): boolean {
  if (!a) return false;
  return normalizeText(a) === normalizeText(b);
}

function passesPrice(doc: SearchDoc, parsed: ParsedQuery): boolean {
  const f = parsed.priceFilter;
  if (!f) return true;
  // A price query is always about a purchasable unit: docs without an indexed
  // price (website pages, guides) cannot satisfy it.
  const price = doc.price ?? null;
  if (price == null || price <= 0) return false;
  if (f.max != null && price > f.max) return false;
  if (f.min != null && price < f.min) return false;
  return true;
}

function passesWeight(doc: SearchDoc, parsed: ParsedQuery): boolean {
  const f = parsed.weightFilter;
  if (!f) return true;
  // A weight query is always about a unit: anything without an indexed dry
  // weight (website pages, guides, feed-only unit pages) cannot satisfy it.
  const weight = doc.dryWeightLbs ?? null;
  if (weight == null || weight <= 0) return false;
  if (f.max != null && weight > f.max) return false;
  if (f.min != null && weight < f.min) return false;
  return true;
}

function normalizeCondition(raw: string | null | undefined): "new" | "used" | null {
  return normalizeConditionImpl(raw);
}

function passesLength(doc: SearchDoc, parsed: ParsedQuery): boolean {
  const f = parsed.lengthFilter;
  if (!f) return true;
  // A length query is about a unit; pages/guides have no indexed length.
  const len = doc.lengthFt ?? null;
  if (len == null || len <= 0) return false;
  if (f.max != null && len > f.max) return false;
  if (f.min != null && len < f.min) return false;
  return true;
}

function normalizeConditionImpl(raw: string | null | undefined): "new" | "used" | null {
  if (!raw) return null;
  const v = normalizeText(raw);
  if (/pre[\s-]?owned|preowned|second[\s-]?hand|used/.test(v)) return "used";
  if (/\bnew\b/.test(v)) return "new";
  return null;
}

function passesCondition(doc: SearchDoc, parsed: ParsedQuery): boolean {
  const f = parsed.conditionFilter;
  if (!f) return true;
  // A new/used query is about a purchasable unit; pages have no condition.
  const cond = normalizeCondition(doc.condition);
  if (!cond) return false;
  return cond === f.value;
}

/** Every requested feature must actually be on the unit. */
function passesFeatures(doc: SearchDoc, parsed: ParsedQuery): boolean {
  if (parsed.featureTags.length === 0) return true;
  const tags = new Set(doc.featureTags ?? []);
  return parsed.featureTags.every((t) => tags.has(t));
}

/** A named body style limits results to units of that style. */
function passesRvType(doc: SearchDoc, parsed: ParsedQuery): boolean {
  const f = parsed.rvTypeFilter;
  if (!f) return true;
  if (doc.source !== "inventory") return false;
  return rvTypeMatches(f.id, doc.rvType, doc.category);
}

export function scoreDoc(
  doc: SearchDoc,
  parsed: ParsedQuery,
  cfg: QueryLanguageConfig,
): { score: number; breakdown: ScoreBreakdown[] } {
  const w = cfg.rankingWeights;
  const breakdown: ScoreBreakdown[] = [];
  const add = (reason: string, points: number) => {
    if (points > 0) breakdown.push({ reason, points });
  };

  const titleLower = normalizeText(doc.title ?? "");
  const tokenSet = new Set(doc.tokens ?? []);
  const keywordText = normalizeText((doc.keywords ?? []).join(" "));
  const summaryText = normalizeText(doc.summary ?? "");
  const pathText = normalizeText(doc.path ?? "");
  const terms = parsed.searchTerms;

  if (terms.length && eq(doc.title, parsed.normalizedQuery)) add("exact query", w.exactQuery);

  const titleTokens = new Set((doc.tokens ?? []).filter((t) => titleLower.includes(t.slice(0, 3))));
  const termsInTitle = terms.filter((t) => titleLower.split(" ").map((x) => stemToken(x, cfg)).includes(t));
  if (terms.length && termsInTitle.length === terms.length) {
    add("all tokens in title", w.allTokensInTitle);
    if (titleLower.split(" ").length === terms.length) add("exact title", w.exactTitle);
  }

  const modelHit = terms.some((t) => eq(doc.model, t)) || (doc.model ? eq(doc.model, terms.join(" ")) : false);
  if (modelHit) add("exact model", w.exactModel);

  const mfrHit =
    terms.some((t) => eq(doc.manufacturer, t) || eq(doc.brand, t)) ||
    (doc.manufacturer ? eq(doc.manufacturer, terms.join(" ")) : false);
  if (mfrHit) add("exact manufacturer", w.exactManufacturer);
  if (modelHit && mfrHit) add("manufacturer + model", w.manufacturerPlusModel);

  for (const phrase of parsed.phrases) {
    if (titleLower.includes(phrase)) {
      add(`phrase in title: "${phrase}"`, w.phraseInTitle);
      break;
    }
  }
  for (const phrase of parsed.phrases) {
    const inStructured =
      keywordText.includes(phrase) ||
      normalizeText(doc.rvType ?? "").includes(phrase) ||
      normalizeText(doc.category ?? "").includes(phrase);
    if (inStructured) {
      add(`phrase in keywords: "${phrase}"`, Math.round(w.phraseInTitle * 0.7));
      break;
    }
  }

  let structured = 0;
  let prefixed = 0;
  let described = 0;
  let pathed = 0;
  for (const t of terms) {
    if (
      tokenSet.has(t) ||
      keywordText.split(" ").includes(t) ||
      eq(doc.stockNumber, t) ||
      eq(doc.floorplan, t) ||
      eq(doc.vin, t) ||
      (doc.year != null && String(doc.year) === t)
    ) {
      structured++;
      continue;
    }
    if (t.length >= 3 && [...tokenSet].some((tok) => tok.startsWith(t))) {
      prefixed++;
      continue;
    }
    if (summaryText.includes(t)) {
      described++;
      continue;
    }
    if (pathText.includes(t)) pathed++;
  }
  add("structured keyword", structured * w.structuredKeyword);
  add("prefix match", prefixed * w.prefix);
  add("description match", described * w.description);
  add("path match", pathed * w.path);

  let expandedHits = 0;
  for (const t of parsed.expandedTerms) if (tokenSet.has(t)) expandedHits++;
  add("synonym/alias match", Math.min(expandedHits, 3) * Math.round(w.structuredKeyword * 0.4));

  if (parsed.years.length && doc.year && parsed.years.includes(doc.year)) {
    add("year match", w.structuredKeyword);
  }
  if (parsed.priceFilter && doc.price) {
    add("price match", Math.round(w.structuredKeyword * 1.5));
  }
  if (parsed.weightFilter && doc.dryWeightLbs) {
    add("dry weight match", Math.round(w.structuredKeyword * 1.5));
  }
  if (parsed.lengthFilter && doc.lengthFt) {
    add("length match", Math.round(w.structuredKeyword * 1.5));
  }
  if (parsed.conditionFilter && normalizeCondition(doc.condition) === parsed.conditionFilter.value) {
    add(`condition match (${parsed.conditionFilter.value})`, Math.round(w.structuredKeyword * 1.5));
  }
  for (const tag of parsed.featureTags) {
    if ((doc.featureTags ?? []).includes(tag)) {
      add(`feature match (${featureLabel(tag)})`, Math.round(w.structuredKeyword * 2));
    }
  }
  if (parsed.rvTypeFilter && rvTypeMatches(parsed.rvTypeFilter.id, doc.rvType, doc.category)) {
    add(`type match (${rvTypeLabel(parsed.rvTypeFilter.id)})`, Math.round(w.structuredKeyword * 2));
  }

  const raw = breakdown.reduce((sum, b) => sum + b.points, 0);
  const typeWeight = cfg.typeWeights[doc.type] ?? cfg.typeWeights[doc.source] ?? 60;
  const score = Math.round(raw * (typeWeight / 100));
  if (titleTokens.size === 0 && raw === 0) return { score: 0, breakdown };
  return { score, breakdown: [...breakdown, { reason: `type weight ${doc.type}`, points: score - raw }] };
}

export function rankCandidates(
  docs: SearchDoc[],
  parsed: ParsedQuery,
  cfg: QueryLanguageConfig,
  limit = cfg.maxResults,
): SearchHit[] {
  const hits: SearchHit[] = [];
  for (const doc of docs) {
    if (!passesPrice(doc, parsed)) continue;
    if (!passesWeight(doc, parsed)) continue;
    if (!passesLength(doc, parsed)) continue;
    if (!passesCondition(doc, parsed)) continue;
    if (!passesFeatures(doc, parsed)) continue;
    if (!passesRvType(doc, parsed)) continue;
    const { score, breakdown } = scoreDoc(doc, parsed, cfg);
    if (score <= 0) continue;
    hits.push({ doc, score, breakdown });
  }
  hits.sort(
    (a, b) =>
      b.score - a.score ||
      (b.doc.year ?? 0) - (a.doc.year ?? 0) ||
      a.doc.title.localeCompare(b.doc.title),
  );
  return hits.slice(0, limit);
}
